import { validateZonePolygon, type ZonePolygonRejection } from "@produce/shared";
import { serverZoneShapeRefusal, zoneShapeProblem } from "./zoneWriteError";

/**
 * How each copy of "Copy from Day N" ended, and what the producer is told
 * afterwards — pure, so the sorting and the wording are tested.
 *
 * zoneCopyPlan decides which zones CAN be copied (a matching stage on the
 * target day); the editor writes them; this module sorts each write into
 * one outcome and turns the lot into at most two toasts. Skipped, refused
 * and failed look the same to a producer — the zone is not there — but
 * they are not the same thing: blaming a write that failed on RLS or the
 * network on "a missing stage" sends them to Step 2 to fix something that
 * is not broken, and a shape the polygon rules refuse needs a redraw, not
 * a retry. One clause per cause, each true.
 */

export type ZoneCopyOutcome =
  | { kind: "copied" }
  /** The shape breaks the polygon rules: the client's check or the server's trigger. */
  | { kind: "refused"; label: string; reason: ZonePolygonRejection | null }
  /** Anything else: an unreadable polygon, RLS, the network. */
  | { kind: "failed" };

/**
 * Before the write: a shape the server's trigger would refuse anyway (an
 * outline drawn before the rules — an upsert is judged as an INSERT), named
 * instead of sent. Null when it may be sent.
 */
export function copyShapeRefusal(polygon: unknown, label: string): ZoneCopyOutcome | null {
  const validation = validateZonePolygon(polygon, "stage_zone");
  return validation.ok === false ? { kind: "refused", label, reason: validation.reason } : null;
}

/** After a failed write: the server refusing the shape (SQLSTATE 22023), or any other failure. */
export function copyWriteFailure(error: unknown, label: string): ZoneCopyOutcome {
  const refusal = serverZoneShapeRefusal(error);
  return refusal ? { kind: "refused", label, reason: refusal.reason } : { kind: "failed" };
}

function zoneCount(n: number): string {
  return `${n} ${n === 1 ? "zone" : "zones"}`;
}

export type ZoneCopyReport = {
  /** For toast.success, or null when nothing was copied. */
  success: string | null;
  /** For toast.warning, or null when every planned zone was copied. */
  warning: string | null;
};

export function zoneCopyReport(input: {
  outcomes: readonly ZoneCopyOutcome[];
  /** zoneCopyPlan's skipped zone names: no matching stage on the target day. */
  skipped: readonly string[];
  /** The day copied from, as the producer counts days (1-based). */
  sourceDay: number;
  /** The day copied onto, 1-based. */
  targetDay: number;
}): ZoneCopyReport {
  const { outcomes, skipped, sourceDay, targetDay } = input;

  const copied = outcomes.filter((outcome) => outcome.kind === "copied").length;
  const refused = outcomes.flatMap((outcome) => (outcome.kind === "refused" ? [outcome] : []));
  const failed = outcomes.filter((outcome) => outcome.kind === "failed").length;

  const clauses: string[] = [];
  if (refused.length > 0) {
    const named = refused
      .map(({ label, reason }) => `${label} (${zoneShapeProblem(reason, "stage_zone")})`)
      .join(", ");
    clauses.push(
      `${zoneCount(refused.length)} not copied — the Day ${sourceDay} shape is not valid, please redraw: ${named}`,
    );
  }
  if (skipped.length > 0) {
    clauses.push(
      `${zoneCount(skipped.length)} skipped — no matching stage on Day ${targetDay} for: ${skipped.join(", ")}`,
    );
  }
  if (failed > 0) {
    clauses.push(`${zoneCount(failed)} could not be written`);
  }

  return {
    success: copied > 0 ? `Copied ${zoneCount(copied)} from Day ${sourceDay}.` : null,
    warning: clauses.length > 0 ? `${clauses.join(". ")}.` : null,
  };
}
